// Live HTML approximation of the JD DOCX output (renderJdDocx), sharing the
// measurement/pagination mechanics with TemplatePreview: build small atomic
// blocks, measure a hidden full-width copy, pack fixed-height pages. Page
// chrome (logo/watermark/footer) mirrors TemplatePreview's — that file is
// the reference for the tricky parts (zero-height measuring wrapper, fixed
// image sizing).

import { useLayoutEffect, useRef, useState } from "react";
import {
  PAPER_SIZES,
  SAMPLE_JD,
  jdContactRows,
  jdHeaderRows,
  resolveJdContact,
  type AgpJobDescription,
  type ContentLine,
  type TemplateDefinition,
} from "@agp/shared";
import { fontStack, paginate, pt, tw } from "./TemplatePreview";

const PAGE_GAP = 18;

interface Block {
  node: React.ReactNode;
  keepWithNext?: boolean;
}

function buildJdBlocks(d: TemplateDefinition, jd: AgpJobDescription): Block[] {
  const heading = `#${d.colors.heading}`;
  const subtle = `#${d.colors.subtle}`;
  const headingFont = fontStack(d.fonts.heading);
  const labelColPx = tw(d.dateColumnTwips);
  const blocks: Block[] = [];
  let key = 0;
  const push = (node: React.ReactNode, keepWithNext?: boolean) =>
    blocks.push({ node, keepWithNext });

  // Word's standard bullet list, mirroring the JD numbering in jdDocx.ts:
  // solid dot at 0.25" (24px), text at 0.5" (48px); sub-level one step in.
  const bulletPt = d.fonts.bulletPt || d.fonts.bodyPt;
  const bullet = (text: string, sub = false) => {
    const textLeft = sub ? 96 : 48;
    const dotLeft = sub ? 72 : 24;
    push(
      <div key={key++} style={{ position: "relative", paddingLeft: textLeft, paddingBottom: 2 }}>
        <span
          style={{
            position: "absolute",
            left: dotLeft,
            // The DOCX bullet is the Symbol-font dot, larger than Arial's "•"
            // at the same point size — hence the 1.2 factor.
            fontSize: pt(sub ? bulletPt : bulletPt * 1.2),
            lineHeight: 1,
          }}
        >
          {sub ? "◦" : "•"}
        </span>
        {text}
      </div>,
    );
  };
  const contentLines = (list: ContentLine[]) => {
    for (const l of list) {
      if (l.kind === "para") {
        push(
          <div key={key++} style={{ paddingBottom: 4 }}>
            {l.text}
          </div>,
        );
      } else {
        bullet(l.text, l.kind === "sub");
      }
    }
  };

  push(
    <div
      key={key++}
      style={{
        fontFamily: headingFont,
        color: heading,
        fontWeight: 700,
        fontSize: pt(d.fonts.namePt),
        lineHeight: 1.1,
        paddingBottom: 14,
      }}
    >
      {jd.title ?? "Position"}
    </div>,
    true,
  );

  for (const [label, value] of jdHeaderRows(jd)) {
    push(
      <div
        key={key++}
        style={{ display: "grid", gridTemplateColumns: `${labelColPx}px 1fr`, paddingBottom: 2 }}
      >
        <b>{label}</b>
        <span>{value}</span>
      </div>,
    );
  }

  for (const section of jd.sections) {
    push(
      <div key={key++} style={{ fontWeight: 700, paddingTop: 12, paddingBottom: 4 }}>
        {section.heading}
      </div>,
      true,
    );
    contentLines(section.lines);
  }

  // Mirrors renderJdDocx: job-level contact wins, else the template's fixed
  // one. Non-null already means it has content. The heading keeps the body
  // style; the lines take the template's contact styling.
  const contact = resolveJdContact(jd, d);
  if (contact) {
    const cs = d.jdContactStyle;
    const contactCss: React.CSSProperties = cs
      ? {
          fontFamily: fontStack(cs.font),
          fontSize: pt(cs.sizePt),
          color: `#${cs.color}`,
          ...(cs.bold ? { fontWeight: 700 } : {}),
        }
      : {};
    const ch = d.jdContactHeading;
    push(
      <div
        key={key++}
        style={{
          fontWeight: ch?.bold !== false ? 700 : 400,
          paddingTop: 18,
          paddingBottom: 5,
          ...(ch ? { fontSize: pt(ch.sizePt), color: `#${ch.color}` } : {}),
        }}
      >
        {ch?.text || "Please Contact:"}
      </div>,
      true,
    );
    if (contact.name) {
      push(
        <div key={key++} style={{ fontWeight: 700, paddingBottom: 1, ...contactCss }}>
          {contact.name}
        </div>,
      );
    }
    if (contact.title) {
      push(
        <div key={key++} style={{ paddingBottom: 1, ...contactCss }}>
          {contact.title}
        </div>,
      );
    }
    if (contact.company) {
      push(
        <div key={key++} style={{ paddingBottom: 1, ...contactCss }}>
          {contact.company}
        </div>,
      );
    }
    for (const [label, value] of jdContactRows(contact)) {
      push(
        <div key={key++} style={{ paddingBottom: 1, ...contactCss }}>
          {label ? <b>{label} </b> : null}
          <span style={label || cs ? undefined : { color: subtle }}>{value}</span>
        </div>,
      );
    }
  }

  return blocks;
}

export function JdPreview({
  definition,
  logoUrl,
  watermarkUrl,
  jd = SAMPLE_JD,
  zoom = 1,
}: {
  definition: TemplateDefinition;
  logoUrl: string | null;
  watermarkUrl: string | null;
  jd?: AgpJobDescription;
  zoom?: number;
}) {
  const d = definition;
  const paper = PAPER_SIZES[d.page.size ?? "A4"];
  const PAGE_W = tw(paper.widthTwips);
  const PAGE_H = tw(paper.heightTwips);
  const contentW = PAGE_W - tw(d.page.marginLeft) - tw(d.page.marginRight);
  const headerTop = Math.max(10, tw(d.page.marginTop) - 34);
  // Sizes are px at 96dpi, the same scale the DOCX embeds images at (docx
  // ImageRun: px × 9525 EMU) and the same scale this page is drawn at.
  const logoDisplayH = logoUrl ? d.branding.logoSize.height : 18;
  const logoFloating = (d.branding.logoLayout ?? "floating") === "floating";
  // A floating logo pins 0.5" (48px) from the page top, exactly where the
  // DOCX anchors it (FLOATING_LOGO_TOP_EMU); inline sits in the header flow.
  const logoTop = logoUrl && logoFloating ? 48 : headerTop;
  // Unlike the resume preview, the JD title spans the full width, so a logo
  // pushes PAGE 1's content down (mirrors the title's spacing-before in
  // jdDocx.ts). Later pages have no logo and start at the top margin.
  const contentTop = logoUrl
    ? Math.max(tw(d.page.marginTop), logoTop + logoDisplayH + 8)
    : tw(d.page.marginTop);
  const capacity = PAGE_H - tw(d.page.marginTop) - tw(d.page.marginBottom);
  const firstCapacity = PAGE_H - contentTop - tw(d.page.marginBottom);

  const wrapRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.7);
  const [pages, setPages] = useState<number[][]>([]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / PAGE_W) * zoom);
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, [PAGE_W, zoom]);

  const blocks = buildJdBlocks(d, jd);
  const keepWithNext = blocks.map((b) => b.keepWithNext === true);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const measure = () => {
      const heights = [...el.children].map((child) => child.getBoundingClientRect().height);
      setPages(paginate(heights, keepWithNext, capacity, firstCapacity));
    };
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(d), jd, capacity, firstCapacity, contentW]);

  const bodyFont = fontStack(d.fonts.body);
  const headingFont = fontStack(d.fonts.heading);
  const heading = `#${d.colors.heading}`;

  const bodyStyle: React.CSSProperties = {
    color: `#${d.colors.text}`,
    fontFamily: bodyFont,
    fontSize: pt(d.fonts.bodyPt),
    lineHeight: 1.35,
  };

  const wmH = d.branding.watermarkHAlign ?? "center";
  const wmV = d.branding.watermarkVAlign ?? "center";
  const wmStyle: React.CSSProperties = {
    position: "absolute",
    width: d.branding.watermarkSize.width,
    height: d.branding.watermarkSize.height,
    pointerEvents: "none",
  };
  if (wmH === "center") wmStyle.left = "50%";
  else if (wmH === "left") wmStyle.left = 0;
  else wmStyle.right = 0;
  if (wmV === "center") wmStyle.top = "50%";
  else if (wmV === "top") wmStyle.top = 0;
  else wmStyle.bottom = 0;
  wmStyle.transform = `translate(${wmH === "center" ? "-50%" : "0"}, ${wmV === "center" ? "-50%" : "0"})`;

  const pageChrome = (pi: number) => (
    <>
      {watermarkUrl && (
        <img
          src={watermarkUrl}
          alt=""
          style={{ ...wmStyle, opacity: d.branding.watermarkOpacity ?? 1 }}
        />
      )}
      {pi === 0 && (
        <div
          style={{
            position: "absolute",
            top: logoTop,
            left: tw(d.page.marginLeft),
            right: tw(d.page.marginRight),
            display: "flex",
            justifyContent: { left: "flex-start", center: "center", right: "flex-end" }[
              d.branding.logoAlign ?? "right"
            ],
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="logo"
              style={{
                width: d.branding.logoSize.width,
                height: d.branding.logoSize.height,
                opacity: d.branding.logoOpacity ?? 1,
              }}
            />
          ) : (
            <span
              style={{ fontFamily: headingFont, color: heading, fontWeight: 700, fontSize: pt(10) }}
            >
              {d.branding.companyName}
            </span>
          )}
        </div>
      )}
      <div
        style={{
          position: "absolute",
          bottom: 14,
          left: tw(d.page.marginLeft),
          right: tw(d.page.marginRight),
          textAlign: "center",
          color: `#${d.branding.footerStyle?.color ?? d.colors.subtle}`,
          fontSize: pt(d.branding.footerStyle?.sizePt ?? 8),
          fontWeight: d.branding.footerStyle?.bold ? 700 : 400,
          fontFamily: fontStack(d.branding.footerStyle?.font ?? d.fonts.body),
          borderTop:
            d.branding.footerStyle?.showRule !== false && d.branding.footerLines.length > 0
              ? `${pt(d.branding.footerStyle?.ruleWidthPt ?? 1)}px solid #${d.branding.footerStyle?.color ?? d.colors.subtle}`
              : undefined,
          paddingTop:
            d.branding.footerStyle?.showRule !== false && d.branding.footerLines.length > 0
              ? pt(d.branding.footerStyle?.ruleSpacingPt ?? 4)
              : 0,
        }}
      >
        {d.branding.footerLines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>
    </>
  );

  const totalH = pages.length * (PAGE_H + PAGE_GAP) * scale;

  return (
    <div ref={wrapRef} className="max-h-[75vh] w-full overflow-x-auto overflow-y-auto rounded-lg">
      <div aria-hidden style={{ position: "relative", height: 0, overflow: "hidden" }}>
        <div
          style={{
            position: "absolute",
            visibility: "hidden",
            pointerEvents: "none",
            width: contentW,
            left: 0,
            top: 0,
            ...bodyStyle,
          }}
          ref={measureRef}
        >
          {blocks.map((b, i) => (
            <div key={i}>{b.node}</div>
          ))}
        </div>
      </div>

      <div style={{ height: totalH, width: PAGE_W * scale }} className="mx-auto overflow-hidden">
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
          {pages.map((pageBlocks, pi) => (
            <div
              key={pi}
              style={{
                width: PAGE_W,
                height: PAGE_H,
                background: "#ffffff",
                position: "relative",
                marginBottom: PAGE_GAP,
                boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
                overflow: "hidden",
                ...bodyStyle,
              }}
            >
              {pageChrome(pi)}
              <div
                style={{
                  position: "absolute",
                  top: pi === 0 ? contentTop : tw(d.page.marginTop),
                  left: tw(d.page.marginLeft),
                  width: contentW,
                  maxHeight: pi === 0 ? firstCapacity : capacity,
                  overflow: "hidden",
                }}
              >
                {pageBlocks.map((bi) => (
                  <div key={bi}>{blocks[bi]?.node}</div>
                ))}
              </div>
              <span
                style={{
                  position: "absolute",
                  right: 10,
                  bottom: 6,
                  fontFamily: "system-ui, sans-serif",
                  fontSize: 9,
                  color: "#B0B3B8",
                }}
              >
                {pi + 1} / {pages.length}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
